import { AxiosError } from 'axios';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { createPenname, fetchPenname, fetchPennames, fetchPennameStories, updatePenname, deletePenname } from '../../services/api/Penname';
import { IPaginationResponseDto, IPenname, IStory, ISuccessResult } from '../../services/types';
import useBackdrop from '../useBackdrop';
import useSnackbar from '../useSnackbar';

enum QueryKey {
  AllPennames = 'allPennames',
  Penname = 'Penname',
  PennameStories = 'PennameStories',
}

interface IUsePennameMutation {
  create: UseMutationResult<IPenname, Error, Partial<IPenname>>;
  update: UseMutationResult<IPenname, Error, Partial<IPenname>>;
  merge: UseMutationResult<ISuccessResult<IPenname>, Error, { penname: Partial<IPenname>; targetPennameId: number }>;
  remove: UseMutationResult<ISuccessResult<IPenname>, Error, Partial<IPenname>>;
}

export const usePennames = (page = 1, limit = 20, searchKey?: string): UseQueryResult<IPaginationResponseDto<IPenname>, AxiosError> =>
  useQuery<IPaginationResponseDto<IPenname>, AxiosError>(
    [QueryKey.AllPennames, { page, limit, searchKey }],
    async () => {
      const [fetchPromise] = fetchPennames(page, searchKey ? undefined : limit, searchKey);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    {
      onError: e => {
        console.log(e.response ? e.response.data.message : e.message);
      },
      keepPreviousData: true,
    },
  );

export const usePenname = (pennameId?: number): UseQueryResult<IPenname | undefined, AxiosError> =>
  useQuery<IPenname | undefined, AxiosError>(
    [QueryKey.Penname, pennameId],
    async () => {
      if (!pennameId) {
        return;
      }

      const [fetchPromise] = fetchPenname(pennameId);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    {
      onError: e => {
        console.log(e.response ? e.response.data.message : e.message);
      },
    },
  );

export const usePennameStories = (pennameId?: number, page = 1, limit = 20): UseQueryResult<IPaginationResponseDto<IStory> | undefined, AxiosError> =>
  useQuery<IPaginationResponseDto<IStory> | undefined, AxiosError>(
    [QueryKey.PennameStories, pennameId, { page, limit }],
    async () => {
      if (!pennameId) {
        return;
      }

      const [fetchPromise] = fetchPennameStories(pennameId, page, limit);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    {
      onError: e => {
        console.log(e.response ? e.response.data.message : e.message);
      },
    },
  );

export const usePennameMutation = (): IUsePennameMutation => {
  const snackbar = useSnackbar();
  const backdrop = useBackdrop();
  const queryClient = useQueryClient();

  const create = useMutation<IPenname, AxiosError, Partial<IPenname>>(
    async (penname: Partial<IPenname>) => {
      backdrop.show();
      const { data: apiResult } = await createPenname(penname);

      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult.data;
    },
    {
      onSuccess: () => {
        snackbar.success('Created successfully.');
        queryClient.invalidateQueries(QueryKey.AllPennames);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  const update = useMutation<IPenname, AxiosError, Partial<IPenname>>(
    async (penname: Partial<IPenname>) => {
      if (!penname.id) {
        throw new Error('id is necessary to update');
      }

      backdrop.show();
      const { data: apiResult } = await updatePenname(penname.id, penname);

      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult.data;
    },
    {
      onSuccess: (data, variables) => {
        snackbar.success('Updated successfully.');
        queryClient.invalidateQueries(QueryKey.AllPennames);
        queryClient.setQueryData([QueryKey.Penname, variables.id], data);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  const merge = useMutation<ISuccessResult<IPenname>, AxiosError, { penname: Partial<IPenname>; targetPennameId: number }>(
    async ({ penname, targetPennameId }) => {
      if (!penname.id || !targetPennameId) {
        throw new Error('id and targetPennameId is necessary to merge');
      }

      backdrop.show();
      const { data: apiResult } = await deletePenname(penname.id, { option: 'merge', targetPennameId });

      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult;
    },
    {
      onSuccess: (data, variables) => {
        snackbar.success('Merged successfully.');
        queryClient.removeQueries([QueryKey.Penname, variables.penname.id]);
        queryClient.invalidateQueries(QueryKey.AllPennames);
        queryClient.invalidateQueries([QueryKey.Penname, variables.targetPennameId]);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  const remove = useMutation<ISuccessResult<IPenname>, AxiosError, Partial<IPenname>>(
    async (penname: Partial<IPenname>) => {
      if (!penname.id) {
        throw new Error('id is necessary to remove');
      }

      backdrop.show();

      const { data: apiResult } = await deletePenname(penname.id, { option: 'delete' });
      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult;
    },
    {
      onSuccess: () => {
        snackbar.success('Deleted successfully.');
        queryClient.invalidateQueries(QueryKey.AllPennames);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  return {
    create,
    update,
    merge,
    remove,
  };
};
